import { getApps, initializeApp, type FirebaseApp } from "firebase/app";
import { connectAuthEmulator, getAuth, type Auth } from "firebase/auth";
import {
  connectFirestoreEmulator,
  getFirestore,
  type Firestore,
} from "firebase/firestore";
import { getMessaging, type Messaging } from "firebase/messaging";

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

export const FIREBASE_VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY;

const firestoreEmulatorHost = import.meta.env.VITE_FIRESTORE_EMULATOR_HOST?.trim();
const firestoreEmulatorPort = Number(
  import.meta.env.VITE_FIRESTORE_EMULATOR_PORT ?? "8682",
);

/**
 * The Auth emulator URL for a local stack, or null. The emulator accepts
 * unsigned custom tokens, so only a dev build ever connects to it, and only for
 * a plain `host:port` (the same shape as the backend's
 * `FIREBASE_AUTH_EMULATOR_HOST`).
 */
export function resolveAuthEmulatorUrl(
  raw: string | undefined,
  isDev: boolean,
): string | null {
  const value = raw?.trim();
  if (!isDev || !value || !/^[\w.-]+:\d{2,5}$/.test(value)) return null;
  return `http://${value}`;
}

const authEmulatorUrl = resolveAuthEmulatorUrl(
  import.meta.env.VITE_FIREBASE_AUTH_EMULATOR_HOST,
  import.meta.env.DEV,
);

let app: FirebaseApp | null = null;
let auth: Auth | null = null;
let firestore: Firestore | null = null;
let messaging: Messaging | null = null;

function isConfigured(): boolean {
  return Boolean(
    firebaseConfig.apiKey &&
      firebaseConfig.projectId &&
      firebaseConfig.messagingSenderId &&
      firebaseConfig.appId,
  );
}

export function getRestaurantFirebaseApp(): FirebaseApp | null {
  if (!isConfigured()) return null;
  try {
    app ??= getApps()[0] ?? initializeApp(firebaseConfig);
    return app;
  } catch (error) {
    console.warn("[restaurant-notifications] Firebase initialization failed", error);
    return null;
  }
}

export function getRestaurantFirebaseAuth(): Auth | null {
  const firebaseApp = getRestaurantFirebaseApp();
  if (!firebaseApp) return null;
  try {
    if (!auth) {
      auth = getAuth(firebaseApp);
      if (authEmulatorUrl) {
        connectAuthEmulator(auth, authEmulatorUrl, { disableWarnings: true });
      }
    }
    return auth;
  } catch (error) {
    console.warn("[restaurant-notifications] Firebase Auth unavailable", error);
    return null;
  }
}

export function getRestaurantFirestore(): Firestore | null {
  const firebaseApp = getRestaurantFirebaseApp();
  if (!firebaseApp) return null;
  try {
    if (!firestore) {
      firestore = getFirestore(firebaseApp);
      if (
        firestoreEmulatorHost &&
        Number.isInteger(firestoreEmulatorPort) &&
        firestoreEmulatorPort > 0
      ) {
        connectFirestoreEmulator(
          firestore,
          firestoreEmulatorHost,
          firestoreEmulatorPort,
        );
      }
    }
    return firestore;
  } catch (error) {
    console.warn("[restaurant-notifications] Firestore unavailable", error);
    return null;
  }
}

export function getRestaurantFirebaseMessaging(): Messaging | null {
  const firebaseApp = getRestaurantFirebaseApp();
  if (!firebaseApp) return null;
  try {
    messaging ??= getMessaging(firebaseApp);
    return messaging;
  } catch (error) {
    // Unsupported browsers and blocked storage can throw during getMessaging.
    console.warn("[restaurant-notifications] Firebase Messaging unavailable", error);
    return null;
  }
}
